import { memo, useMemo, useState } from 'react'
import { useField, UseFieldConfig } from 'react-final-form'
import { Checkbox, IconButton } from '@cargomart/ui'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import { useGetCommonOperationsQuery, useGetParamOperationsQuery } from 'modules/order/api'
import IconPlus from 'remixicon/icons/System/add-line.svg?react'
import IconMinus from 'remixicon/icons/System/subtract-fill.svg?react'
import { OperationItem } from 'schema'
import { NumberInput } from 'shared/components/NumberInput'
import { isNumber } from 'shared/helpers/number'

import { OrderCreateOperationForm } from '../components/OrderCreatePointOperation/types'

interface IProps extends Pick<UseFieldConfig<OrderCreateOperationForm[]>, 'validate'> {
  name: string
  className?: string
  label?: string
}

const Skeleton = () =>
  [...new Array(5)].map((_, i) => (
    <div key={i} className="mb-2 flex items-start gap-3">
      <PlaceholderCell className="!w-8" height={24} />
      <div className="flex w-full cursor-pointer flex-col">
        <PlaceholderCell className="mb-1" height={24} />
        <PlaceholderCell height={20} />
      </div>
    </div>
  ))

const fieldClassName =
  'w-[72px] [&>*:first-child]:text-center  [&>*:first-child]:!text-on-surface [&>*]:!text-on-surface-variant'

interface IAmountStepperProps {
  amount: number
  error: boolean
  maxAmount: number
  onChange: (amount: number) => void
}

const AmountStepper = function AmountStepper({ amount, error, maxAmount, onChange }: IAmountStepperProps) {
  const [draft, setDraft] = useState<string | null>(null)

  const handleChange = (value: string) => {
    setDraft(value)

    const parsed = parseInt(value, 10)

    if (parsed > 0) {
      onChange(parsed)
    }
  }

  const handleBlur = () => {
    setDraft(null)
  }

  const handleStep = (delta: 1 | -1) => {
    const base = draft === null ? amount : parseInt(draft, 10)

    if (!Number.isInteger(base)) return

    onChange(Math.min(Math.max(base + delta, 1), maxAmount))
    setDraft(null)
  }

  return (
    <div className="grid grid-cols-[1fr_auto_auto] gap-x-1">
      <IconButton outline disabled={!isNumber(amount) || amount <= 1} onClick={() => handleStep(-1)}>
        <IconMinus size="xs" />
      </IconButton>
      <NumberInput
        className={fieldClassName}
        error={error}
        max={maxAmount}
        min={1}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={() => setDraft(String(amount))}
        postfix="шт"
        value={draft ?? String(amount)}
      />
      <IconButton outline disabled={!isNumber(amount) || amount >= maxAmount} onClick={() => handleStep(1)}>
        <IconPlus size="xs" />
      </IconButton>
    </div>
  )
}

export const FieldOrderCreateCommonOperation = memo<IProps>(function FieldOrderCreateCommonOperation({
  name,
  className,
  validate
}) {
  const { input, meta } = useField<OrderCreateOperationForm[]>(name, {
    validate
  })
  const { data: commonOperations = [], isLoading } = useGetCommonOperationsQuery()
  const { data: commonOperationParams = [], isLoading: isParamLoading } = useGetParamOperationsQuery()

  const maxAmountByOperationId = useMemo(() => {
    const map: Record<string, number> = {}

    commonOperationParams.forEach((param) => {
      map[param.operationId] = param.maxAmount > 1 ? param.maxAmount : 1
    })

    return map
  }, [commonOperationParams])

  if (isLoading || isParamLoading) {
    return (
      <div className={className}>
        <div className="flex select-none flex-col py-4">
          <Skeleton />
        </div>
      </div>
    )
  }

  if (!commonOperations || commonOperations.length === 0) {
    return null
  }

  const operations = input.value || []

  const getSelectedOperation = (operationId: string) =>
    operations.find((op) => op.operationId === operationId && !op.terminalId)

  const handleToggle = (operation: OperationItem) => {
    const isChecked = !!getSelectedOperation(operation.id)

    if (isChecked) {
      input.onChange(operations.filter((op) => !(op.operationId === operation.id && !op.terminalId)))
    } else {
      input.onChange([...operations, { operationId: operation.id, name: operation.name, amount: 1 }])
    }
  }

  const handleAmountChange = (operationId: string, amount: number) => {
    input.onChange(operations.map((op) => (op.operationId === operationId && !op.terminalId ? { ...op, amount } : op)))
  }

  return (
    <div className={className}>
      <div className="flex flex-col gap-2">
        {commonOperations.map((operation) => {
          const selectedOp = getSelectedOperation(operation.id)
          const isChecked = !!selectedOp
          const maxAmount = maxAmountByOperationId[operation.id] ?? 1

          return (
            <div key={operation.id} className="flex items-start gap-3">
              <Checkbox checked={isChecked} id={`common-${operation.id}`} onChange={() => handleToggle(operation)} />
              <label className="flex flex-1 cursor-pointer flex-col" htmlFor={`common-${operation.id}`}>
                <span className="text-label-md leading-6 text-on-surface">{operation.name}</span>
                {operation.description && (
                  <span className="text-body-sm text-on-surface-variant">
                    {`${operation.description}${operation.unitDescription ? '.' : ''}`}
                  </span>
                )}
                {operation.unitDescription && (
                  <span className="text-body-sm text-on-surface-variant">
                    {`Единица услуги: ${operation.unitDescription}${operation.description ? '.' : ''}`}
                  </span>
                )}
              </label>
              {isChecked && maxAmount > 1 && selectedOp && (
                <AmountStepper
                  amount={selectedOp.amount}
                  error={!isNumber(selectedOp.amount) || selectedOp.amount < 1 || selectedOp.amount > maxAmount}
                  maxAmount={maxAmount}
                  onChange={(amount) => handleAmountChange(operation.id, amount)}
                />
              )}
            </div>
          )
        })}
      </div>

      {meta.touched && meta.error && (
        <div className="text-error mt-2 text-label-md">
          {typeof meta.error === 'string' ? meta.error : 'Необходимо выбрать услуги'}
        </div>
      )}
    </div>
  )
})
